<template>
  <div ref="container" class="vue-bpmn-diagram-container" id="modelerContainer">
    <div class="tooling">
      <v-btn class="btn" text small @click="resetZoom()">
        <v-icon dark> center_focus_weak </v-icon>
      </v-btn>
      <v-divider style="margin: 2.5px"></v-divider>
      <v-btn class="btn" text small @click="zoomIn()">
        <v-icon dark> mdi-plus </v-icon>
      </v-btn>
      <v-divider style="margin: 2.5px"></v-divider>
      <v-btn class="btn" text small @click="zoomOut()">
        <v-icon dark> mdi-minus </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
import NavigatedViewer from "prime-custom-modeling/lib/NavigatedViewer";
//Modeler dependencies and css files
import "prime-custom-modeling/dist/assets/diagram-js.css";
import "prime-custom-modeling/dist/assets/bpmn-font/css/bpmn.css";

import customElementExtension from "@/resources/customElementType.json";

import customModelingElementService from "@/components/customModelingElementsService";
import { getBusinessObject } from "prime-custom-modeling/lib/util/ModelUtil";
export default {
  components: {},
  props: {
    diagramXML: {
      type: String,
    },
    elementsToLabel: {
      type: Array,
    },
    evalMode: {
      type: Boolean,
    },
  },
  data: function () {
    return {};
  },
  updated() {
    console.log("Updated");
  },
  mounted: function () {
    this.initViewer();
  },
  beforeDestroy: function () {
    if (this.BpmnModeler) this.BpmnModeler.destroy();
  },
  watch: {
    diagramXML: function (val) {
      if (this.BpmnModeler == null) return;
      this.BpmnModeler.importXML(val);
    },
    elementsToLabel: function (val) {
      this.setBadges();
    },
  },
  methods: {
    //called from parent AnswerQuestions
    colorElementInModel(elementId, stroke, fill) {
      const element = this.BpmnModeler.get("elementRegistry").get(elementId);
      if (element == null) return;
      const businessObject = element.businessObject;

      businessObject.di.set("stroke", stroke);
      businessObject.di.set("fill", fill);
      businessObject.di.set("opacity", 0.2);

      const gfx = this.BpmnModeler.get("elementRegistry").getGraphics(element);

      const type = element.waypoints ? "connection" : "shape";
      const graphix = this.BpmnModeler.get("graphicsFactory");
      graphix.update(type, element, gfx);
    },
    //called from parent AnswerQuestions
    resetColorsInModel() {
      const elements = this.BpmnModeler.get("elementRegistry").getAll();

      if (elements == null) return;

      elements.forEach((element) => {
        if (element == null) return;
        const businessObject = element.businessObject;

        businessObject.di.set("stroke", "");
        businessObject.di.set("fill", "");

        const gfx =
          this.BpmnModeler.get("elementRegistry").getGraphics(element);

        const type = element.waypoints ? "connection" : "shape";
        const graphix = this.BpmnModeler.get("graphicsFactory");
        graphix.update(type, element, gfx);
      });
    },
    async initViewer() {
      const customModelingElements =
        await customModelingElementService.getCustomModelingElements();
      this.$store.dispatch("initModelingElements", customModelingElements.data);

      let container = this.$refs.container;
      let self = this;
      if (this.BpmnModeler != null) this.BpmnModeler.destroy();

      this.BpmnModeler = new NavigatedViewer({
        container: container,
        moddleExtensions: {
          qa: customElementExtension,
        },
      });

      this.BpmnModeler.on("import.done", function (event) {
        const error = event.error;
        const warnings = event.warnings;

        if (error) {
          self.$emit("error", error);
        } else {
          self.$emit("shown", warnings);
          let eventBus = self.BpmnModeler.get("eventBus");
          eventBus.on("element.click", function (clickEvent) {
            self.$emit("elementClicked", clickEvent.element);
          });

          eventBus.on("element.contextmenu", (event) => {
            event.preventDefault();
            event.stopPropagation();
            const businessObject = getBusinessObject(event.element);
            let element = event.element;
            if (element.type == "bpmn:SubProcess") {
              const businessObject = getBusinessObject(element);
              let uuid = businessObject.$attrs.subProcess;
              if (uuid == null) return;
              self.$emit("switchModel", uuid);
            }
          });

          self.BpmnModeler.get("canvas").zoom("fit-viewport", "auto");
          let rootElement = self.BpmnModeler._definitions.rootElements[0];
          self.$emit("elementClicked", rootElement);
          self.setBadges();
          if (self.evalMode) {
            self.setEvaluationBadges();
          } else {
            self.hideEvaluationBadges();
          }
        }
      });
      if (this.BpmnModeler == null) return;
      this.BpmnModeler.importXML(this.diagramXML);
    },
    resetZoom() {
      if (this.BpmnModeler)
        this.BpmnModeler.get("canvas").zoom("fit-viewport", "auto");
    },
    zoomIn() {
      if (this.BpmnModeler) this.BpmnModeler.get("zoomScroll").stepZoom(1);
    },
    zoomOut() {
      if (this.BpmnModeler) this.BpmnModeler.get("zoomScroll").stepZoom(-1);
    },
    setBadges() {
      let overlays = this.BpmnModeler.get("overlays");
      let elementRegistry = this.BpmnModeler.get("elementRegistry");
      this.$emit("createBadge", overlays, elementRegistry, true);
    },
    setEvaluationBadges() {
      let overlays = this.BpmnModeler.get("overlays");
      let elementRegistry = this.BpmnModeler.get("elementRegistry");
      this.$emit("createEvaluationBadge", overlays, elementRegistry);
    },
    hideEvaluationBadges() {
      let overlays = this.BpmnModeler.get("overlays");
      let elementRegistry = this.BpmnModeler.get("elementRegistry");
      this.$emit("hideEvaluationBadges", overlays, elementRegistry);
    },
    exportPDF() {
      return new Promise((resolve, reject) => {
        this.BpmnModeler.saveSVG({ format: true }, (err, svg) => {
          if (err) {
            reject(err);
          }
          resolve(svg);
        });
      });
    },
    generateReport() {
      const elementRegistry = this.BpmnModeler.get("elementRegistry");
      const overlaysService = this.BpmnModeler.get("overlays");
      const elements = elementRegistry.getAll();

      return new Promise((resolve, reject) => {
        this.BpmnModeler.saveSVG({ format: true }, (err, svg) => {
          if (err) {
            reject(err);
            return;
          }

          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svg, "image/svg+xml");
          const svgRoot = svgDoc.querySelector("svg");

          const allOverlays = overlaysService._overlays;
          Object.values(allOverlays).forEach(function (overlay) {
            const element = overlay.element;
            if (!element) return;

            const position = overlay.position || {};

            const htmlEl =
              overlay.html && overlay.html[0] ? overlay.html[0] : overlay.html;

            const text = htmlEl
              ? (htmlEl.textContent || htmlEl.innerText || "").trim()
              : "";

            if (!text || !/^\d+$/.test(text)) return;

            var x, y;

            if (position.right !== undefined) {
              x =
                (element.x || 0) + (element.width || 0) - (position.right || 0);
            } else {
              x = (element.x || 0) + (position.left || 0);
            }

            if (position.bottom !== undefined) {
              y =
                (element.y || 0) +
                (element.height || 0) -
                (position.bottom || 0);
            } else {
              y = (element.y || 0) + (position.top || 0);
            }

            var badgeWidth = text.length > 1 ? 22 : 16;
            var badgeHeight = 16;

            var rect = svgDoc.createElementNS(
              "http://www.w3.org/2000/svg",
              "rect"
            );
            rect.setAttribute("x", x - badgeWidth / 2);
            rect.setAttribute("y", y - badgeHeight);
            rect.setAttribute("width", badgeWidth);
            rect.setAttribute("height", badgeHeight);
            rect.setAttribute("rx", 3); // border-radius: 3px
            rect.setAttribute("fill", "#eeeeee");
            rect.setAttribute("stroke", "#aaaaaa");
            rect.setAttribute("stroke-width", "1");

            var svgText = svgDoc.createElementNS(
              "http://www.w3.org/2000/svg",
              "text"
            );
            svgText.setAttribute("x", x);
            svgText.setAttribute("y", y - 4);
            svgText.setAttribute("text-anchor", "middle");
            svgText.setAttribute("font-size", "12"); // font-size: 12px
            svgText.setAttribute("font-family", "Arial, sans-serif");
            svgText.setAttribute("fill", "#000000");
            svgText.textContent = text;

            svgRoot.appendChild(rect);
            svgRoot.appendChild(svgText);
          });

          const serializer = new XMLSerializer();
          resolve({
            svgData: serializer.serializeToString(svgDoc),
            elements,
          });
        });
      });
    },
    exportXML() {
      return new Promise((resolve, reject) => {
        this.BpmnModeler.cleanXML({ format: true }, (err, xml) => {
          if (err) {
            reject(err);
          }
          resolve(xml);
        });
      });
    },
    resetColorsInModel() {
      const elements = this.BpmnModeler.get("elementRegistry").getAll();

      if (elements == null) return;

      elements.forEach((element) => {
        if (element == null) return;
        const businessObject = element.businessObject;

        businessObject.di.set("stroke", "");
        businessObject.di.set("fill", "");

        const gfx =
          this.BpmnModeler.get("elementRegistry").getGraphics(element);

        const type = element.waypoints ? "connection" : "shape";
        const graphix = this.BpmnModeler.get("graphicsFactory");
        graphix.update(type, element, gfx);
      });
    },
    colorElementInModel(elementId, stroke, fill) {
      const element = this.BpmnModeler.get("elementRegistry").get(elementId);
      if (element == null) return;
      const businessObject = element.businessObject;

      businessObject.di.set("stroke", stroke);
      businessObject.di.set("fill", fill);
      businessObject.di.set("opacity", 0.2);

      const gfx = this.BpmnModeler.get("elementRegistry").getGraphics(element);

      const type = element.waypoints ? "connection" : "shape";
      const graphix = this.BpmnModeler.get("graphicsFactory");
      graphix.update(type, element, gfx);
    },
  },
};
</script>
<style scoped>
.vue-bpmn-diagram-container {
  width: 100%;
  background-color: white;
}

.highlight-overlay {
  background-color: green; /* color elements as green */
  opacity: 0.4;
  pointer-events: none; /* no pointer events, allows clicking through onto the element */
}
.highlight:not(.djs-connection) .djs-visual > :nth-child(1) {
  fill: green !important; /* color elements as green */
}
.badge {
  position: absolute;
  top: -10px;
  right: -10px;
  padding: 5px 10px;
  border-radius: 50%;
  background: red;
  color: white;
}
.tooling {
  border: 1px solid lightgray;
  position: absolute;
  bottom: 75px;
  right: 15px;
  z-index: 100;
  background-color: white;
  text-align-last: center;
}
.btn {
  min-width: 0px !important;
  height: 28px;
  width: 28px;
}
#modelerContainer {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background-color: white;
}
</style>